/**
 * card-collaborators-store — UI-1 / TD-V2-PERM-01.
 *
 * React Query hooks pra consumir endpoints do PR #24 PERM-1:
 *   - GET    /api/v1/cards/:cardId/collaborators
 *   - POST   /api/v1/cards/:cardId/collaborators/:userId
 *   - DELETE /api/v1/cards/:cardId/collaborators/:userId
 *
 * Feature flag CARD_COLLABORATORS_ENABLED gateaa POST/DELETE no backend.
 * GET sempre OK; UI deve ler do query.error pra detectar 503 e mostrar
 * mensagem de feature-flag.
 */

import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';

export interface CardCollaborator {
  userId: string;
  nome: string;
  fotoUrl: string | null;
  addedAtIso: string;
  addedByUserId: string | null;
}

export const CARD_COLLABORATORS_KEYS = {
  list: (cardId: string) => ['v1', 'cards', cardId, 'collaborators'] as const,
};

export function useCardCollaborators(cardId: string | null) {
  return useQuery({
    queryKey: CARD_COLLABORATORS_KEYS.list(cardId ?? ''),
    queryFn: () =>
      api.get<CardCollaborator[]>(
        `/api/v1/cards/${encodeURIComponent(cardId!)}/collaborators`,
      ),
    enabled: !!cardId && hasToken(),
    staleTime: 30_000,
  });
}

export function useCardCollaboratorActions(cardId: string) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const invalidate = useCallback(
    () => qc.invalidateQueries({ queryKey: CARD_COLLABORATORS_KEYS.list(cardId) }),
    [qc, cardId],
  );

  const add = useMutation({
    mutationFn: async (userId: string) => {
      setPending(`add-${userId}`);
      try {
        return await api.post<{ ok: true; cardId: string; userId: string }>(
          `/api/v1/cards/${encodeURIComponent(cardId)}/collaborators/${encodeURIComponent(userId)}`,
          {},
        );
      } finally {
        setPending(null);
      }
    },
    onSuccess: () => invalidate(),
  });

  const remove = useMutation({
    mutationFn: async (userId: string) => {
      setPending(`del-${userId}`);
      try {
        return await api.delete<{ ok: true }>(
          `/api/v1/cards/${encodeURIComponent(cardId)}/collaborators/${encodeURIComponent(userId)}`,
        );
      } finally {
        setPending(null);
      }
    },
    onSuccess: () => invalidate(),
  });

  return { add, remove, pending };
}
